<template>
  <div class="orders-layout">

    <!-- Left panel: order list -->
    <div class="orders-panel">
      <div class="orders-panel__head">
        <h2 class="orders-panel__title">Mis ordenes</h2>
        <div class="orders-panel__count">{{ filteredOrders.length }} pedidos</div>

        <!-- Comedor filter -->
        <select v-model="comedorFilter" class="orders-filter">
          <option value="">Todos los comedores</option>
          <option v-for="c in centers" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <div class="orders-list">
        <!-- Empty state -->
        <div v-if="orders.length === 0" class="orders-empty">
          No hay pedidos recientes.
        </div>

        <button
          v-for="o in filteredOrders"
          :key="o.id"
          @click="$emit('update:selected', o.id)"
          class="order-row"
          :class="{ 'order-row--selected': o.id === selected }"
        >
          <div class="order-row__top">
            <span class="order-row__id">#{{ o.id }}</span>
            <span class="order-row__date">Pedido: {{ fmtDate(o.ordered_at) }}</span>
          </div>
          <div class="order-row__bottom">
            <div>
              <div class="order-row__center">{{ o.center_name }}</div>
              <div :class="['order-status', `order-status--${statusModifier(o.status_id)}`]">
                <span :class="['order-status__dot', `order-status__dot--${statusModifier(o.status_id)}`]" />
                {{ statusLabel(o.status_id) }}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Right detail panel -->
    <div class="orders-detail">
      <div class="orders-detail__inner">

        <!-- Back button -->
        <button @click="$emit('back')" class="back-btn">
          <span class="back-btn__icon">
            <Icon name="chev" :size="14" />
          </span>
          Volver a productos
        </button>

        <!-- Empty / no selection -->
        <div v-if="!selFull" class="detail-empty">
          Selecciona un pedido de la lista.
        </div>

        <template v-else>
          <!-- Order title row -->
          <div class="detail-title-row">
            <div>
              <h1 class="detail-order-id">Orden #{{ selFull.id }}</h1>
              <!-- Date chips -->
              <div class="detail-dates">
                <span>
                  <strong class="detail-dates__label">Pedido:</strong>
                  {{ fmtDate(selFull.ordered_at) }}
                </span>
                <span v-if="selFull.delivery_date">
                  <strong class="detail-dates__label">Entrega:</strong>
                  {{ fmtDate(selFull.delivery_date) }}
                </span>
              </div>
              <div class="detail-center-name">{{ selFull.center_name }}</div>
            </div>

            <!-- PDF download button -->
            <button v-if="!detailLoading" @click="downloadPdf" class="btn-pdf">
              <Icon name="receipt" :size="14" /> Descargar PDF
            </button>
          </div>

          <!-- Tracking bar -->
          <div class="tracking-bar">
            <div class="tracking-steps">
              <div
                v-for="(s, i) in trackingSteps"
                :key="s"
                class="tracking-step"
                :class="{ 'tracking-step--done': i < statusStep }"
              >
                <div
                  v-if="i > 0"
                  class="tracking-line"
                  :class="{ 'tracking-line--done': i < statusStep }"
                />
                <div
                  class="tracking-dot"
                  :class="{ 'tracking-dot--done': i < statusStep }"
                >{{ i < statusStep ? '✓' : i + 1 }}</div>
                {{ s }}
              </div>
            </div>
          </div>

          <!-- Order detail lines -->
          <div class="detail-lines">
            <!-- Loading state -->
            <div v-if="detailLoading" class="detail-loading">Cargando detalle…</div>

            <!-- Detail lines table -->
            <template v-else-if="detailLines.length > 0">
              <!-- Table header -->
              <div class="detail-table-header">
                <span>Producto</span>
                <span class="text-center">Cant.</span>
                <span class="text-right">Precio</span>
              </div>
              <!-- Rows -->
              <div
                v-for="line in detailLines"
                :key="line.id"
                class="detail-table-row"
              >
                <div>
                  <div class="detail-product-name">{{ line.product?.name || line.product_name || '—' }}</div>
                  <div v-if="line.product?.code" class="detail-product-code">{{ line.product.code }}</div>
                </div>
                <div class="text-center detail-qty">{{ line.quantity }}</div>
                <div class="text-right detail-price">
                  {{ line.unit_price != null ? `$${Number(line.unit_price).toFixed(2)}` : '—' }}
                </div>
              </div>
              <!-- Total row -->
              <div v-if="selFull.total != null" class="detail-total-row">
                <span>Total</span>
                <span />
                <span class="text-right">${{ Number(selFull.total).toFixed(2) }}</span>
              </div>
            </template>

            <!-- No lines -->
            <div v-else class="detail-no-lines">Sin productos en este pedido.</div>
          </div>

        </template>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Icon from './ui/Icon.vue'
import { getOrder, getOrders } from '../services/orders.js'

const props = defineProps({
  selected: { type: [String, Number], default: null },
  store:    { type: Object, required: true },
})

defineEmits(['update:selected', 'back'])

const orders = computed(() => props.store.orders.value || [])

// ── Comedor filter — API-side filtering by client_id ─────────────────────────
const comedorFilter  = ref('')
const filterLoading  = ref(false)
const filteredByApi  = ref(null)

const centers = computed(() => props.store.centers?.value || [])

const filteredOrders = computed(() =>
  filteredByApi.value !== null ? filteredByApi.value : orders.value
)

const sel = computed(() => filteredOrders.value.find(o => o.id === props.selected) || null)

watch(comedorFilter, async (centerId) => {
  if (!centerId) {
    filteredByApi.value = null
    return
  }
  filterLoading.value = true
  try {
    const raw  = await getOrders({ client_id: centerId, per_page: 50 })
    const list = (raw?.orders || (Array.isArray(raw) ? raw : []))
    filteredByApi.value = list.map(o => ({
      ...o,
      status_id:   o.order_status_id ?? o.status_id ?? null,
      center_name: o.client?.business_name || o.client?.name || '',
      clientId:    o.client?.id ?? o.client_id ?? null,
    }))
  } catch (e) {
    console.warn('[OrdersList] filter error:', e)
    filteredByApi.value = null
  } finally {
    filterLoading.value = false
  }
})

// ── Order detail lazy-load ──────────────────────────────────────────────────
const detailLoading = ref(false)
const detailLines   = ref([])
const detailExtra   = ref({})

const selFull = computed(() => sel.value ? { ...sel.value, ...detailExtra.value } : null)

watch(
  () => props.selected,
  async (id) => {
    detailLines.value = []
    detailExtra.value = {}
    if (!id) return
    const existing = sel.value?.order_details || sel.value?.details || []
    if (existing.length > 0) {
      detailLines.value = existing
      return
    }
    detailLoading.value = true
    try {
      const res = await getOrder(id)
      const full = res?.order || res || {}
      detailLines.value = full.order_details || full.details || []
      const detailStatus = full.order_status_id ?? full.status_id ?? null
      detailExtra.value = {
        ...(full.delivery_date != null ? { delivery_date: full.delivery_date } : {}),
        ...(full.total        != null ? { total:        full.total        } : {}),
        ...(detailStatus      != null ? { status_id:    detailStatus      } : {}),
      }
    } catch (e) {
      console.warn('[OrdersList] detail fetch error:', e)
    } finally {
      detailLoading.value = false
    }
  },
  { immediate: true }
)

// ── Helpers ─────────────────────────────────────────────────────────────────
// status_id raw: 1=Levantado, 2=Orden Embarque, 3=Surtido, 4=Cargado, 5=Entregado
//               6=Rechazado, 7=Liberado, 8=No Entregado
// Cliente no ve 2, 7, 8 — se mapean:  2→1  7→1  8→6
function normalizeStatus(sid) {
  if (sid === 2 || sid === 7) return 1
  if (sid === 8) return 6
  return sid || 1
}

const trackingSteps = ['Levantado', 'Surtido', 'Cargado', 'Entregado']
const STEP_FOR_STATUS = { 1: 1, 3: 2, 4: 3, 5: 4 }

const statusStep = computed(() => {
  const sid = normalizeStatus(selFull.value?.status_id)
  if (sid === 6) return trackingSteps.length
  return STEP_FOR_STATUS[sid] || 1
})

function fmtDate(val) {
  if (!val) return '—'
  return String(val).split(' ')[0]
}

function statusLabel(sid) {
  const map = { 1: 'Levantado', 3: 'Surtido', 4: 'Cargado', 5: 'Entregado', 6: 'Rechazado' }
  return map[normalizeStatus(sid)] || 'Pendiente'
}

// Returns CSS modifier string for status — used in class bindings
function statusModifier(sid) {
  const n = normalizeStatus(sid)
  if (n === 5) return 'delivered'
  if (n === 6) return 'rejected'
  return 'progress'
}

function downloadPdf() {
  const o = selFull.value
  if (!o) return

  const lines = detailLines.value.map(line => {
    const name     = line.product?.name || line.product_name || '—'
    const code     = line.product?.code ? `<div style="font-size:11px;color:#888">${line.product.code}</div>` : ''
    const qty      = line.quantity ?? '—'
    const price    = line.unit_price != null ? `$${Number(line.unit_price).toFixed(2)}` : '—'
    const subtotal = (line.unit_price != null && line.quantity != null)
      ? `$${(Number(line.unit_price) * Number(line.quantity)).toFixed(2)}`
      : '—'
    return `<tr>
      <td><div style="font-weight:600">${name}</div>${code}</td>
      <td style="text-align:center">${qty}</td>
      <td style="text-align:right">${price}</td>
      <td style="text-align:right">${subtotal}</td>
    </tr>`
  }).join('')

  const totalRow = o.total != null
    ? `<tr style="font-weight:700;border-top:2px solid #ddd">
        <td colspan="3" style="text-align:right;padding-top:12px">Total</td>
        <td style="text-align:right;padding-top:12px">$${Number(o.total).toFixed(2)}</td>
      </tr>`
    : ''

  const deliveryRow = o.delivery_date
    ? `<div><span style="color:#888;font-size:11px">Entrega</span><br>${fmtDate(o.delivery_date)}</div>`
    : ''

  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"/>
<title>Orden #${o.id}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;color:#111;padding:40px;font-size:13px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px}
  .brand{font-size:20px;font-weight:700}
  .brand-sub{color:#888;font-size:12px;margin-top:2px}
  h1{font-size:22px;font-weight:700;margin-bottom:6px}
  .meta{color:#555;margin-bottom:24px;display:flex;gap:12px;flex-wrap:wrap;align-items:center}
  .badge{background:#f0fdf4;color:#16a34a;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700}
  table{width:100%;border-collapse:collapse;margin-top:8px}
  th{text-align:left;border-bottom:2px solid #ddd;padding:8px 8px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#666;font-weight:700}
  td{padding:10px 8px;border-bottom:1px solid #eee;vertical-align:top}
  @media print{body{padding:20px}}
</style></head><body>
<div class="header">
  <div>
    <div class="brand">Sicamex</div>
    <div class="brand-sub">Sistema de pedidos</div>
  </div>
  <div style="text-align:right;font-size:12px">
    <div><span style="color:#888;font-size:11px">Pedido</span><br>${fmtDate(o.ordered_at)}</div>
    ${deliveryRow}
  </div>
</div>
<h1>Orden #${o.id}</h1>
<div class="meta">
  <span>${o.center_name || ''}</span>
  <span class="badge">${statusLabel(o.status_id)}</span>
</div>
<table>
  <thead><tr>
    <th>Producto</th>
    <th style="text-align:center">Cant.</th>
    <th style="text-align:right">Precio unit.</th>
    <th style="text-align:right">Subtotal</th>
  </tr></thead>
  <tbody>${lines}${totalRow}</tbody>
</table>
</body></html>`

  const w = window.open('', '_blank', 'width=800,height=600')
  w.document.write(html)
  w.document.close()
  w.focus()
  setTimeout(() => { w.print() }, 300)
}
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────────────────────────── */
.orders-layout {
  display: flex;
  height: 100%;
}

/* ─── Left panel ─────────────────────────────────────────────────────────────── */
.orders-panel {
  width: 320px;
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.orders-panel__head {
  padding: 22px 22px 14px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.orders-panel__title {
  margin: 0;
  font-family: var(--ff-head);
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
}

.orders-panel__count {
  margin-top: 4px;
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
}

.orders-filter {
  margin-top: 10px;
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text);
  background: var(--surface);
  cursor: pointer;
}

.orders-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.orders-empty {
  padding: 32px 22px;
  text-align: center;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

/* ─── Order row ──────────────────────────────────────────────────────────────── */
.order-row {
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: 14px 22px;
  background: transparent;
  border: none;
  border-left: 3px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.order-row--selected {
  background: var(--primary-softer);
  border-left-color: var(--primary);
}

.order-row__top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.order-row__id {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}

.order-row__date {
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
}

.order-row__bottom {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-row__center {
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

/* ─── Status badge ───────────────────────────────────────────────────────────── */
.order-status {
  margin-top: 6px;
  font-family: var(--ff-body);
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 5px;
}

.order-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  display: inline-block;
}

/* Status modifiers */
.order-status--delivered    { color: var(--primary); }
.order-status--rejected     { color: #dc2626; }
.order-status--progress     { color: #ff8a3b; }
.order-status__dot--delivered { background: var(--primary); }
.order-status__dot--rejected  { background: #dc2626; }
.order-status__dot--progress  { background: #ff8a3b; }

/* ─── Right detail panel ─────────────────────────────────────────────────────── */
.orders-detail {
  flex: 1;
  overflow-y: auto;
  background: var(--bg);
}

.orders-detail__inner {
  padding: 28px 36px;
}

/* ─── Back button ────────────────────────────────────────────────────────────── */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px 0 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 13px;
  color: var(--text-2);
  margin-bottom: 14px;
}

.back-btn__icon {
  transform: rotate(180deg);
  display: inline-flex;
}

/* ─── Empty / no selection ───────────────────────────────────────────────────── */
.detail-empty {
  margin-top: 60px;
  text-align: center;
  font-family: var(--ff-body);
  font-size: 14px;
  color: var(--text-2);
}

/* ─── Detail header ──────────────────────────────────────────────────────────── */
.detail-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.detail-order-id {
  margin: 0;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 26px;
  color: var(--text);
  letter-spacing: -0.3px;
}

.detail-dates {
  margin-top: 8px;
  display: flex;
  gap: 16px;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

.detail-dates__label {
  color: var(--text);
  font-weight: 600;
}

.detail-center-name {
  margin-top: 4px;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

.btn-pdf {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 13px;
  color: var(--text);
  flex-shrink: 0;
}

/* ─── Tracking bar ───────────────────────────────────────────────────────────── */
.tracking-bar {
  margin-top: 22px;
  padding: 20px;
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
}

.tracking-steps {
  display: flex;
  justify-content: space-between;
}

.tracking-step {
  flex: 1;
  text-align: center;
  position: relative;
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
  font-weight: 700;
}

.tracking-step--done {
  color: var(--primary);
}

.tracking-line {
  position: absolute;
  left: -50%;
  top: 9px;
  width: 100%;
  height: 2px;
  background: var(--border-strong);
}

.tracking-line--done {
  background: var(--primary);
}

.tracking-dot {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  margin: 0 auto 6px;
  background: var(--border-strong);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.tracking-dot--done {
  background: var(--primary);
}

/* ─── Detail lines table ─────────────────────────────────────────────────────── */
.detail-lines {
  margin-top: 22px;
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  overflow: hidden;
}

.detail-loading {
  padding: 24px;
  text-align: center;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

.detail-table-header {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--border);
  font-family: var(--ff-body);
  font-size: 11px;
  font-weight: 700;
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-table-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border);
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text);
  align-items: center;
}

.detail-product-name {
  font-weight: 600;
}

.detail-product-code {
  font-size: 11px;
  color: var(--text-2);
  margin-top: 2px;
}

.detail-qty {
  color: var(--text-2);
}

.detail-price {
  color: var(--text-2);
}

.detail-total-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  padding: 12px 20px;
  font-family: var(--ff-body);
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  background: var(--bg);
}

.detail-no-lines {
  padding: 24px;
  text-align: center;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
}

/* ─── Utility ────────────────────────────────────────────────────────────────── */
.text-center { text-align: center; }
.text-right  { text-align: right; }
</style>
